import { HTMLAttributes, ReactNode, useMemo, useRef } from 'react'
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'

import type { UseParticipantsOptions } from '@/src/hooks'
import { useGridLayout, usePagination, useSwipe } from '@/src/hooks'

import { mergeProps } from '@/src/utils'

import { TrackLoop } from '@/src/components/TrackLoop'
import { PaginationControl } from '@/src/components/controls/PaginationControl'
import { PaginationIndicator } from '@/src/components/controls/PaginationIndicator'

/** @public */
export interface GridLayoutProps
  extends HTMLAttributes<HTMLDivElement>,
    Pick<UseParticipantsOptions, 'updateOnlyOn'> {
  children: ReactNode
  tracks: TrackReferenceOrPlaceholder[]
  isPiP?: boolean
}

/**
 * The `GridLayout` component displays the nested participants in a grid where every participants has the same size.
 * It also supports pagination if there are more participants than the grid can display.
 * @remarks
 * To ensure visual stability when tiles are reordered due to track updates,
 * the component uses the `useVisualStableUpdate` hook.
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <GridLayout tracks={tracks}>
 *     <ParticipantTile />
 *   </GridLayout>
 * <LiveKitRoom>
 * ```
 * @public
 */
export const GridLayout = ({ tracks, isPiP = false, ...props }: GridLayoutProps) => {
  const gridEl = useRef<HTMLDivElement>(null)

  const elementProps = useMemo(
    () => mergeProps(props, { className: `${props?.className} lk-grid-layout` }),
    [props],
  )
  const { layout } = useGridLayout(gridEl, tracks?.length, isPiP)
  const maxTiles = useMemo(
    () => (isPiP ? 2 : layout?.columns * layout?.rows),
    [isPiP, layout?.columns, layout?.rows],
  )
  const pagination = usePagination(maxTiles, tracks)

  useSwipe(gridEl, {
    onLeftSwipe: pagination.nextPage,
    onRightSwipe: pagination.prevPage,
  })

  return (
    <div ref={gridEl} data-lk-pagination={pagination.totalPageCount > 1} {...elementProps}>
      <TrackLoop tracks={pagination.tracks}>{props.children}</TrackLoop>

      {tracks?.length > maxTiles && (
        <>
          <PaginationIndicator
            totalPageCount={pagination.totalPageCount}
            currentPage={pagination.currentPage}
          />

          <PaginationControl pagesContainer={gridEl} {...pagination} />
        </>
      )}
    </div>
  )
}
